/**
 * Tactical Hints Database
 * 
 * Comprehensive collection of tactical hints organized by theme.
 * Used by the TacticalThemeClassifier to provide contextual guidance.
 */

import { TacticalHint } from '../types/chess';

/**
 * Database of tactical hints organized by theme
 */
export const TACTICAL_HINTS: Record<string, TacticalHint[]> = {
  fork: [
    {
      type: 'general',
      text: 'Look for squares where one piece can attack multiple enemy pieces simultaneously',
      priority: 1,
      category: 'pattern'
    },
    {
      type: 'specific',
      text: 'Knights are excellent forking pieces due to their unique movement pattern',
      priority: 2,
      category: 'pattern'
    },
    {
      type: 'specific',
      text: 'Check if the forking piece is protected or can escape after the fork',
      priority: 3,
      category: 'calculation'
    },
    {
      type: 'warning',
      text: 'Ensure your forking piece cannot be captured by a less valuable piece',
      priority: 2,
      category: 'calculation'
    },
    {
      type: 'encouragement',
      text: 'Forks are powerful tactics that can win material or create decisive advantages',
      priority: 4,
      category: 'strategy'
    }
  ],

  pin: [
    {
      type: 'general',
      text: 'Identify pieces that cannot move without exposing more valuable pieces behind them',
      priority: 1,
      category: 'pattern'
    },
    {
      type: 'specific',
      text: 'Bishops, rooks, and queens can create pins along ranks, files, and diagonals',
      priority: 2,
      category: 'pattern'
    },
    {
      type: 'specific',
      text: 'Absolute pins involve the king - the pinned piece cannot legally move',
      priority: 2,
      category: 'pattern'
    },
    {
      type: 'specific',
      text: 'Relative pins involve other valuable pieces - moving may lose material',
      priority: 3,
      category: 'pattern'
    },
    {
      type: 'warning',
      text: 'Be careful not to pin your own pieces to your king',
      priority: 2,
      category: 'calculation'
    },
    {
      type: 'encouragement',
      text: 'Pins can immobilize enemy pieces and create tactical opportunities',
      priority: 4,
      category: 'strategy'
    }
  ],

  discoveredAttack: [
    {
      type: 'general',
      text: 'Look for pieces that can move to reveal an attack from a piece behind them',
      priority: 1,
      category: 'pattern'
    },
    {
      type: 'specific',
      text: 'The moving piece can attack a different target while revealing the hidden attack',
      priority: 2,
      category: 'pattern'
    },
    {
      type: 'specific',
      text: 'Discovered checks are particularly powerful as they force the opponent to respond',
      priority: 2,
      category: 'pattern'
    },
    {
      type: 'warning',
      text: 'Ensure the piece you move to create the discovery is not hanging',
      priority: 3,
      category: 'calculation'
    },
    {
      type: 'encouragement',
      text: 'Discovered attacks can win material by attacking two targets at once',
      priority: 4,
      category: 'strategy'
    }
  ],

  sacrifice: [
    {
      type: 'general',
      text: 'Consider giving up material to gain a significant positional advantage',
      priority: 1,
      category: 'strategy'
    },
    {
      type: 'specific',
      text: 'Sacrifices often expose the enemy king or create unstoppable attacks',
      priority: 2,
      category: 'strategy'
    },
    {
      type: 'specific',
      text: 'Calculate carefully to ensure the sacrifice leads to concrete benefits',
      priority: 2,
      category: 'calculation'
    },
    {
      type: 'warning',
      text: 'Only sacrifice if you can see a clear path to advantage or checkmate',
      priority: 1,
      category: 'calculation'
    },
    {
      type: 'encouragement',
      text: 'Bold sacrifices can turn losing positions into winning attacks',
      priority: 4,
      category: 'strategy'
    }
  ],

  checkmate: [
    {
      type: 'general',
      text: 'Look for ways to attack the enemy king while cutting off all escape squares',
      priority: 1,
      category: 'pattern'
    },
    {
      type: 'specific',
      text: 'Use multiple pieces to coordinate the attack on the king',
      priority: 2,
      category: 'strategy'
    },
    {
      type: 'specific',
      text: 'Control key squares around the king to prevent escape',
      priority: 2,
      category: 'strategy'
    },
    {
      type: 'warning',
      text: 'Verify that the king cannot block the check or capture the attacking piece',
      priority: 1,
      category: 'calculation'
    },
    {
      type: 'encouragement',
      text: 'Checkmate is the ultimate goal - when you see it, go for it!',
      priority: 3,
      category: 'strategy'
    }
  ],

  combination: [
    {
      type: 'general',
      text: 'Look for sequences of moves that combine multiple tactical themes',
      priority: 1,
      category: 'pattern'
    },
    {
      type: 'specific',
      text: 'Combinations often involve forcing moves like checks, captures, and threats',
      priority: 2,
      category: 'strategy'
    },
    {
      type: 'warning',
      text: 'Calculate all variations carefully before committing to a combination',
      priority: 1,
      category: 'calculation'
    },
    {
      type: 'encouragement',
      text: 'Mastering combinations is key to tactical improvement',
      priority: 4,
      category: 'strategy'
    }
  ],

  unknown: [
    {
      type: 'general',
      text: 'Analyze the position carefully to identify tactical opportunities',
      priority: 1,
      category: 'pattern'
    },
    {
      type: 'specific',
      text: 'Look for loose pieces, undefended squares, and piece coordination',
      priority: 2,
      category: 'pattern'
    },
    {
      type: 'encouragement',
      text: 'Every position has tactical possibilities - keep looking!',
      priority: 3,
      category: 'strategy'
    }
  ]
};

/**
 * Gets hints for a specific tactical theme
 * @param theme The tactical theme
 * @returns Array of hints for the theme
 */
export function getHintsForTheme(theme: string): TacticalHint[] {
  return TACTICAL_HINTS[theme] || TACTICAL_HINTS.unknown;
}

/**
 * Gets hints filtered by type
 * @param theme The tactical theme
 * @param type The hint type to filter by
 * @returns Array of hints matching the type
 */
export function getHintsByType(theme: string, type: TacticalHint['type']): TacticalHint[] {
  const hints = getHintsForTheme(theme);
  return hints.filter(hint => hint.type === type);
}

/**
 * Gets the highest priority hints for a theme
 * @param theme The tactical theme
 * @param maxHints Maximum number of hints to return
 * @returns Array of highest priority hints
 */
export function getTopHints(theme: string, maxHints: number = 3): TacticalHint[] {
  const hints = getHintsForTheme(theme);
  return hints
    .sort((a, b) => a.priority - b.priority)
    .slice(0, maxHints);
}

export default {
  TACTICAL_HINTS,
  getHintsForTheme,
  getHintsByType,
  getTopHints
}; 